import pick from 'lodash.pick';
import Color from 'color';
import hoistNonReactStatic from 'hoist-non-react-statics';
import findIndex from 'lodash.findindex';
import xor from 'lodash.xor';
import remark from 'remark';
import remarkReact from 'remark-react';
import classNames from 'classnames';
import omit from 'lodash.omit';
import crown, { withClassNames, ThemeProvider, withPrefix } from '@energysavvy/react-crown';
import PropTypes from 'prop-types';
import React from 'react';
import { CSSTransitionGroup } from 'react-transition-group';
var styles = {
  "alert": "Alert__alert___12caE _boxsizing__borderBox___2GxsM",
  "error": "Alert__error___34dNm Alert__alert___12caE _boxsizing__borderBox___2GxsM",
  "notification": "Alert__notification___1et-O Alert__alert___12caE _boxsizing__borderBox___2GxsM",
  "standard": "Alert__standard___2dY8p Alert__alert___12caE _boxsizing__borderBox___2GxsM",
  "compact": "Alert__compact___2NeqD Alert__alert___12caE _boxsizing__borderBox___2GxsM"
};

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/Alert/Alert.js               ***!
/*!*********************************************!*/

function Alert(_ref) {
  var text = _ref.text,
    alertLevel = _ref.alertLevel,
    size = _ref.size;
  var markdown = remark().use(remarkReact).processSync(text).contents;
  return React.createElement('div', {
    className: classNames(styles[alertLevel], styles[size])
  }, React.cloneElement(markdown));
}
Alert.LEVEL = {
  ERROR: 'error',
  NOTIFICATION: 'notification'
};
Alert.SIZE = {
  STANDARD: 'standard',
  COMPACT: 'compact'
};
Alert.displayName = 'Alert';
Alert.propTypes = {
  text: PropTypes.string.isRequired,
  alertLevel: PropTypes.oneOf([Alert.LEVEL.ERROR, Alert.LEVEL.NOTIFICATION]),
  size: PropTypes.oneOf([Alert.SIZE.STANDARD, Alert.SIZE.COMPACT])
};
Alert.defaultProps = {
  alertLevel: Alert.LEVEL.NOTIFICATION,
  size: Alert.SIZE.STANDARD
};
var styles$1 = {
  "bp-medium": "(min-width: 768px)",
  "structure-base": "#b8bbbc",
  "text-quiet": "#6f8590",
  "common": "BaseButton__common___T2C5m _boxsizing__borderBox___2GxsM",
  "compact": "BaseButton__compact___i34ZD BaseButton__common___T2C5m _boxsizing__borderBox___2GxsM",
  "standard": "BaseButton__standard___3mjTB BaseButton__common___T2C5m _boxsizing__borderBox___2GxsM",
  "continue": "BaseButton__continue___Y6xN6 BaseButton__common___T2C5m _boxsizing__borderBox___2GxsM",
  "narrowWidth": "BaseButton__narrowWidth___2pLu6 BaseButton__common___T2C5m _boxsizing__borderBox___2GxsM",
  "autoWidth": "BaseButton__autoWidth___1DJ3H BaseButton__common___T2C5m _boxsizing__borderBox___2GxsM",
  "wideWidth": "BaseButton__wideWidth___1VlQz BaseButton__common___T2C5m _boxsizing__borderBox___2GxsM"
};

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/Button/BaseButton.css.raw    ***!
/*!*********************************************!*/

var themeCSS = "/**\n * This file is never intended to be included in the css bundle.\n * styleSelector, and the other variables are intended to be replaced by\n * intended to be replaced by themeable in @energysavvy/react-crown\n */\n.styleSelector {\n    background-color: primaryColor;\n    color: textOnPrimary;\n}\n\n.styleSelector:focus,\n.styleSelector:hover,\n.styleSelector:disabled {\n    border-color: primaryColorDark;\n    box-shadow: inset 0 0 0 2px textOnPrimary, inset 0 1px 2px primaryColor, 0 0 5px -1px primaryColorDark;\n}\n";
var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};

/**
 * A basic button stylized to Customer standards
 *
 * @example
 * <BaseButton text="Continue" variation={BaseButton.STANDARD} type="button" />
 * // A React Component stylized with no arrow, as a standard button
 * @example
 *  (
        <BaseButton text="Continue" variation={BaseButton.CONTINUE} type="submit" theme={{
            primaryColor: '#1f3d00',
            primaryColorDark: '#123000',
            textOnPrimary: 'white',
        }} />
    )
 * // A React Component stylized with arrow, which will submit a form that contains it
 * // BaseButton is themed green with white text
 * @param {object} props - Customization options for BaseButton
 * @returns {React.Component} A stylized button
 */
function BaseButton(_ref) {
  var children = _ref.children,
    text = _ref.text,
    crownClassName = _ref.crownClassName,
    disabled = _ref.disabled,
    padding = _ref.padding,
    icon = _ref.icon,
    width = _ref.width,
    isLink = _ref.isLink,
    style = _ref.style,
    otherProps = _ref.otherProps,
    className = _ref.className;
  var colorClassName = disabled ? styles$1.disabled : crownClassName;
  var classes = classNames(padding, icon, width, colorClassName, className);
  var Component = isLink ? 'a' : 'button';
  return React.createElement(Component, _extends({
    className: classes,
    'aria-disabled': disabled,
    disabled: disabled,
    style: pick(style, 'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft')
  }, otherProps), text || children);
}
BaseButton.displayName = 'BaseButton';
BaseButton.PADDING = {
  STANDARD: styles$1.standard,
  COMPACT: styles$1.compact
};
BaseButton.ICON = {
  NONE: styles$1.none,
  CONTINUE: styles$1.continue
};
BaseButton.WIDTH = {
  FIT_CONTENT: styles$1.narrowWidth,
  AUTO_RESIZE: styles$1.autoWidth,
  FILL_PARENT: styles$1.wideWidth
};
BaseButton.propTypes = {
  children: function children(_ref2) {
    var text = _ref2.text,
      _children = _ref2.children;
    // eslint-disable-line consistent-return
    if (!(text || _children) || !!(text && _children)) {
      return new Error('Must provide either text or children');
    }
  },
  text: PropTypes.string,
  crownClassName: PropTypes.string,
  disabled: PropTypes.bool,
  padding: PropTypes.oneOf(Object.values(BaseButton.PADDING)),
  icon: PropTypes.oneOf(Object.values(BaseButton.ICON)),
  width: PropTypes.oneOf(Object.values(BaseButton.WIDTH)),
  isLink: PropTypes.bool,
  otherProps: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  style: PropTypes.shape({
    margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    marginTop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    marginRight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    marginBottom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    marginLeft: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  className: PropTypes.string
};
BaseButton.defaultProps = {
  disabled: false,
  padding: BaseButton.PADDING.STANDARD,
  icon: BaseButton.ICON.NONE,
  width: BaseButton.WIDTH.AUTO_RESIZE,
  isLink: false,
  style: {}
};
function transformTheme(theme) {
  return {
    textOnPrimary: theme.textOnPrimary,
    primaryColor: theme.primaryColor,
    primaryColorDark: new Color(theme.primaryColor).darken(0.05).hexString()
  };
}
var BaseButton$1 = crown(themeCSS, transformTheme)(BaseButton);

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/Button/AnchorButton.js       ***!
/*!*********************************************!*/

/**
 * Anchor tag styled as a button, it supports the same theme variations as the Button Component
 * @param {object} props - Customization options for AnchorButton
 * @returns {React.Component} A stylized anchor tag
 */
function AnchorButton(_ref) {
  var children = _ref.children,
    text = _ref.text,
    link = _ref.link,
    newWindow = _ref.newWindow,
    icon = _ref.icon,
    padding = _ref.padding,
    width = _ref.width,
    theme = _ref.theme,
    onClick = _ref.onClick,
    onKeyDown = _ref.onKeyDown,
    onMouseDown = _ref.onMouseDown,
    style = _ref.style,
    className = _ref.className;
  return React.createElement(BaseButton$1, {
    children: children,
    text: text,
    icon: icon,
    padding: padding,
    width: width,
    theme: theme,
    otherProps: {
      href: link,
      target: newWindow ? '_blank' : '_self',
      onClick: onClick,
      onKeyDown: onKeyDown,
      onMouseDown: onMouseDown
    },
    style: style,
    className: className,
    isLink: true
  });
}
AnchorButton.displayName = 'AnchorButton';
AnchorButton.propTypes = {
  children: PropTypes.node,
  link: PropTypes.string.isRequired,
  text: PropTypes.string,
  icon: BaseButton$1.propTypes.icon,
  padding: BaseButton$1.propTypes.padding,
  width: BaseButton$1.propTypes.width,
  newWindow: PropTypes.bool,
  theme: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  onMouseDown: PropTypes.func,
  style: BaseButton$1.propTypes.style,
  className: BaseButton$1.propTypes.className
};
AnchorButton.defaultProps = {
  newWindow: false
};
AnchorButton.PADDING = BaseButton$1.PADDING;
AnchorButton.ICON = BaseButton$1.ICON;
AnchorButton.WIDTH = BaseButton$1.WIDTH;

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/Button/Button.js             ***!
/*!*********************************************!*/

function Button(_ref) {
  var children = _ref.children,
    text = _ref.text,
    disabled = _ref.disabled,
    onClick = _ref.onClick,
    type = _ref.type,
    padding = _ref.padding,
    icon = _ref.icon,
    width = _ref.width,
    theme = _ref.theme,
    style = _ref.style,
    className = _ref.className;
  return React.createElement(BaseButton$1, {
    children: children,
    text: text,
    padding: padding,
    icon: icon,
    disabled: disabled,
    width: width,
    theme: theme,
    style: style,
    className: className,
    otherProps: {
      type: type,
      onClick: onClick
    }
  });
}
Button.propTypes = {
  children: PropTypes.node,
  text: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['submit', 'button', 'reset']),
  padding: BaseButton$1.propTypes.padding,
  icon: BaseButton$1.propTypes.icon,
  width: BaseButton$1.propTypes.width,
  style: BaseButton$1.propTypes.style,
  theme: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  className: BaseButton$1.propTypes.className
};
Button.displayName = 'Button';
Button.PADDING = BaseButton$1.PADDING;
Button.ICON = BaseButton$1.ICON;
Button.WIDTH = BaseButton$1.WIDTH;
var styles$2 = {
  "bp-medium": "(min-width: 768px)",
  "active-orange": "#f3963c",
  "input": "Input__input___3gx0U undefined",
  "radio": "Input__radio___2GyKg Input__input___3gx0U undefined undefined _visuallyhidden__visuallyhidden___GfPHq"
};
var _extends$1 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
function Input(props) {
  var slug = props.slug,
    type = props.type,
    name = props.name,
    value = props.value,
    onChange = props.onChange,
    describedBy = props.describedBy,
    isInvalid = props.isInvalid,
    isRequired = props.isRequired;
  var propKeys = Object.keys(Input.propTypes);
  var extraHTMLAttrs = omit(props, propKeys);
  var ariaAttrs = {
    'aria-describedby': describedBy,
    'aria-invalid': isInvalid,
    'aria-required': isRequired
  };
  return React.createElement('input', _extends$1({
    className: type === 'radio' ? styles$2.radio : styles$2.input,
    name: name || slug,
    id: slug,
    type: type,
    value: value,
    onChange: onChange
  }, extraHTMLAttrs, ariaAttrs));
}
Input.propTypes = {
  slug: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  describedBy: PropTypes.string,
  isInvalid: PropTypes.bool,
  isRequired: PropTypes.bool
};

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/LoadingSpinner/index.js      ***!
/*!*********************************************!*/

// Styles defined at https://projects.evoworx.org/customer/customer-routing/blob/master/customer_routing/whitelabel/spinner.py
function LoadingSpinner() {
  return React.createElement("div", {
    className: "LoadingSpinner-wrapper"
  }, React.createElement("div", {
    className: "LoadingSpinner-loader"
  }));
}
var styles$3 = {
  "dark-orange": "#c46f00",
  "dark-gray": "#888",
  "lighter-gray": "#eee",
  "text": "HelpText__text___1bj3E",
  "icon": "HelpText__icon___aL0GW"
};

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/questions/HelpText.js        ***!
/*!*********************************************!*/

function HelpText(_ref) {
  var className = _ref.className,
    id = _ref.id,
    prefix = _ref.prefix,
    text = _ref.text;
  return React.createElement('div', {
    className: classNames(styles$3.text, className),
    id: id
  }, React.createElement('span', {
    className: styles$3.icon
  }, prefix + "\u2009"), text);
}
HelpText.displayName = 'HelpText';
HelpText.propTypes = {
  id: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  className: PropTypes.string,
  prefix: PropTypes.string
};
HelpText.defaultProps = {
  prefix: 'Tip'
};
var styles$4 = {
  "bp-medium": "(min-width: 768px)",
  "light-gray": "#e3e3e3",
  "active-orange": "#f3963c",
  "mid-orange": "#ea861c",
  "dark-orange": "#c46f00",
  "mint-green": "#f5f8ef",
  "taupe": "#e2e0c7",
  "visuallyhidden": "_visuallyhidden__visuallyhidden___GfPHq",
  "focusable": "_visuallyhidden__focusable___3RYRJ",
  "tile": "ChoiceTile__tile___2qCLR",
  "input": "ChoiceTile__input___1nF3t _visuallyhidden__visuallyhidden___GfPHq _visuallyhidden__focusable___3RYRJ",
  "tileContents": "ChoiceTile__tileContents___2-ft9",
  "icon": "ChoiceTile__icon___3GYmm",
  "label": "ChoiceTile__label___SyUCJ",
  "tileContentsIcon": "ChoiceTile__tileContentsIcon___7ZJSQ"
};

/*!**************************************************************!*\
  !***  @energysavvy/customer-ui-components                   ***!
  !***    ./src/questions/types/SelectQuestion/ChoiceTile.js  ***!
/*!**************************************************************!*/

function ChoiceTile(_ref) {
  var choice = _ref.choice,
    name = _ref.name,
    id = _ref.id,
    isSelected = _ref.isSelected,
    onChange = _ref.onChange,
    inputType = _ref.inputType;
  var iconElement = choice.icon ? React.createElement('img', {
    src: choice.icon,
    className: styles$4.icon,
    alt: '',
    draggable: false
  }) : null;
  var tileIconClass = choice.icon ? styles$4.tileContentsIcon : null;
  var tileContentClasses = classNames(styles$4.tileContents, tileIconClass);
  return React.createElement('label', {
    className: styles$4.tile
  }, React.createElement('input', {
    type: inputType,
    'aria-labelledby': id,
    className: styles$4.input,
    name: name,
    value: choice.slug,
    checked: isSelected,
    onChange: onChange
  }), React.createElement('div', {
    className: tileContentClasses
  }, iconElement, React.createElement('span', {
    id: id,
    className: styles$4.label
  }, choice.label)));
}
ChoiceTile.propTypes = {
  choice: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string
  }).isRequired,
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  inputType: PropTypes.oneOf(['radio', 'checkbox'])
};
ChoiceTile.defaultProps = {
  inputType: 'radio'
};
var styles$5 = {
  "bp-medium": "(min-width: 768px)",
  "group": "BaseSelectQuestion__group___34qzc"
};

/*!**********************************************************************!*\
  !***  @energysavvy/customer-ui-components                           ***!
  !***    ./src/questions/types/SelectQuestion/BaseSelectQuestion.js  ***!
/*!**********************************************************************!*/

function BaseSelectQuestion(_ref) {
  var labelId = _ref.labelId,
    _ref$question = _ref.question,
    questionSlug = _ref$question.slug,
    choices = _ref$question.choices,
    className = _ref.className,
    helpTextId = _ref.helpTextId,
    handleChange = _ref.handleChange,
    choiceType = _ref.choiceType,
    isSelected = _ref.isSelected;
  var choiceTiles = choices.map(function (choice) {
    var choiceId = questionSlug + '-' + choice.slug;
    return React.createElement(ChoiceTile, {
      key: choiceId,
      choice: choice,
      name: questionSlug + '-choice',
      id: choiceId,
      isSelected: isSelected(choice.slug),
      inputType: choiceType,
      onChange: handleChange
    });
  });
  return React.createElement('div', {
    'aria-describedby': helpTextId,
    'aria-labelledby': labelId,
    role: 'radiogroup',
    className: classNames(styles$5.group, className)
  }, choiceTiles);
}
BaseSelectQuestion.propTypes = {
  labelId: PropTypes.string.isRequired,
  question: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    question_text: PropTypes.string.isRequired,
    choices: PropTypes.arrayOf(ChoiceTile.propTypes.choice).isRequired
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  choiceType: PropTypes.string.isRequired,
  isSelected: PropTypes.func.isRequired,
  className: PropTypes.string,
  helpTextId: PropTypes.string
};
BaseSelectQuestion.defaultProps = {
  currentAnswer: null
};
var _extends$2 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
function SelectQuestion(props) {
  var value = props.value,
    onValueChange = props.onValueChange; // eslint-disable-line react/prop-types

  return React.createElement(BaseSelectQuestion, _extends$2({
    handleChange: function handleChange(event) {
      return onValueChange(event.target.value);
    },
    choiceType: 'radio',
    isSelected: function isSelected(choiceSlug) {
      return value === choiceSlug;
    }
  }, props));
}
SelectQuestion.propTypes = Object.assign(omit(BaseSelectQuestion.propTypes, 'handleChange', 'choiceType', 'isSelected'), {
  onValueChange: PropTypes.func.isRequired,
  value: PropTypes.string
});

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/utils/index.js               ***!
/*!*********************************************!*/

/**
 * A function which translates a value in a given range to a percentage
 *
 * @param {object} range - numeric range within which input falls
 * @param {number} value - value to translate to percent
 * @returns {number} a decimal value from 0-100
 */
function mapRangeValueToPercent() {
  var range = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    start: 0,
    end: 100
  };
  var value = arguments[1];
  return 100 / (range.end - range.start) * (value - range.start);
}
var styles$6 = {
  "bp-medium": "(min-width: 768px)",
  "quiet-text": "rgba(0, 0, 0, 0.38)",
  "structure-faint": "rgba(0, 0, 0, 0.12)",
  "structure-light": "rgba(0, 0, 0, 0.26)",
  "structure-medium": "rgba(0, 0, 0, 0.38)",
  "wrapper": "SliderInput__wrapper___xGQK_",
  "inputWrapper": "SliderInput__inputWrapper___3Uuhq",
  "iconStart": "SliderInput__iconStart___1Zcrs",
  "iconEnd": "SliderInput__iconEnd___28KA5",
  "icon": "SliderInput__icon___3Qd0s",
  "startIcon": "SliderInput__startIcon___3CceO SliderInput__icon___3Qd0s",
  "endIcon": "SliderInput__endIcon___1ikTT SliderInput__icon___3Qd0s",
  "fill": "SliderInput__fill___12-6Z",
  "input": "SliderInput__input___1n178"
};

/*!*************************************************!*\
  !***  @energysavvy/customer-ui-components      ***!
  !***    ./src/SliderInput/SliderInput.css.raw  ***!
/*!*************************************************!*/

var themeCSS$1 = "/**\n * This file is never intended to be included in the css bundle.\n * styleSelector, and the other variables are intended to be replaced by\n * generateThemeElement in @energysavvy/react-crown\n */\n\n.styleSelector .fill {\n    background: primaryColor;\n}\n\n.styleSelector .icon {\n    color: iconColor;\n}\n\n.styleSelector .input[type='range']::-ms-thumb {\n    background: primaryColor;\n}\n\n.styleSelector .input[type='range']:focus::-ms-thumb {\n    box-shadow: 0 0 0 12px rgba(primaryColorRgb, 0.12);\n}\n\n.styleSelector .input[type='range']::-webkit-slider-thumb {\n    background: primaryColor;\n}\n\n.styleSelector .input[type='range']:focus::-webkit-slider-thumb {\n    box-shadow: 0 0 0 13px rgba(primaryColorRgb, 0.12);\n}\n\n.styleSelector .input[type='range']::-moz-range-thumb {\n    background: primaryColor;\n}\n\n.styleSelector .input[type='range']:focus::-moz-range-thumb {\n    box-shadow: 0 0 0 13px rgba(primaryColorRgb, 0.12);\n}\n\n@media bp-medium {\n    .input[type='range']:focus::-ms-thumb {\n        box-shadow: 0 0 0 10px rgba(primaryColorRgb, 0.12);\n    }\n\n    .input[type='range']:focus::-webkit-slider-thumb {\n        box-shadow: 0 0 0 10px rgba(primaryColorRgb, 0.12);\n    }\n\n    .input[type='range']:focus::-moz-range-thumb {\n        box-shadow: 0 0 0 10px rgba(primaryColorRgb, 0.12);\n    }\n}\n";
var _extends$3 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
var NON_PASSTHROUGH_PROPS = ['className', 'crownClassName', 'theme', 'onValueChange', 'onValueBlur', 'startIcon', 'endIcon', 'valueLabel'];
/**
 * A basic input[type=range] styled to Customer standards
 *
 * @example
 * <SliderInput name="range" id="range-id" value={50} onValueChange={() => {}} onValueBlur={() => {}}
/>
 * // A React Component styled as a slider
 * @example
 * (
 *      <SliderInput
 *          name="range"
 *          id="range-id"
 *          min={10}
 *          max={30}
 *          step={2}
 *          value={18}
 *          onValueChange={() => {}}
 *          onValueBlur={() => {}}
 *      />
 * )
 * // A React Component styled as a slider with min, max, and step options
 * @param {object} props - Customization options for SliderInput
 * @returns {React.Component} A slider input UI widget
 */
function SliderInput(props) {
  var _classNames2;
  var range = {
    start: props.min,
    end: props.max
  };
  var fillStyle = {
    width: mapRangeValueToPercent(range, props.value) + '%'
  };
  var _handleChange = function _handleChange(event) {
    var input = event.target;
    // Convert the string returned by reading DOM
    var value = parseFloat(input.value);
    props.onValueChange(value);
  };
  var _handleBlur = function _handleBlur(event) {
    var input = event.target;
    var value = parseFloat(input.value);
    props.onValueBlur(value);
  };
  var _generateElement = function _generateElement(extraProps) {
    var baseProps = omit(props, NON_PASSTHROUGH_PROPS);
    return React.createElement('input', _extends$3({
      type: 'range'
    }, extraProps, baseProps));
  };
  var iconElements = [props.startIcon ? React.createElement('i', {
    key: props.name + '-start-icon',
    className: styles$6.startIcon
  }, props.startIcon) : null, props.endIcon ? React.createElement('i', {
    key: props.name + '-end-icon',
    className: styles$6.endIcon
  }, props.endIcon) : null];
  var outerWrapperClasses = classNames(styles$6.wrapper, _defineProperty({}, props.crownClassName, !props.disabled));
  var inputWrapperClasses = classNames(styles$6.inputWrapper, (_classNames2 = {}, _defineProperty(_classNames2, styles$6.iconStart, !!props.startIcon), _defineProperty(_classNames2, styles$6.iconEnd, !!props.endIcon), _classNames2));
  return React.createElement('div', {
    className: outerWrapperClasses
  }, iconElements, React.createElement('div', {
    className: inputWrapperClasses
  }, _generateElement({
    className: styles$6.input,
    onChange: _handleChange,
    onTouchMove: _handleChange,
    onBlur: _handleBlur,
    onMouseUp: _handleBlur,
    onTouchEnd: _handleBlur,
    onKeyUp: _handleBlur,
    'aria-valuemin': props.min,
    'aria-valuemax': props.max,
    'aria-valuenow': props.value,
    'aria-valuetext': props.valueLabel
  }), React.createElement('span', {
    className: styles$6.fill,
    style: fillStyle
  })));
}
SliderInput.displayName = 'SliderInput';
SliderInput.propTypes = {
  crownClassName: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  // eslint-disable-line react/no-unused-prop-types
  onValueChange: PropTypes.func.isRequired,
  onValueBlur: PropTypes.func,
  disabled: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  // eslint-disable-line react/no-unused-prop-types
  value: PropTypes.number.isRequired,
  startIcon: PropTypes.string,
  // Unicode icon font reference
  endIcon: PropTypes.string,
  valueLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
SliderInput.defaultProps = {
  min: 0,
  max: 100,
  step: 1,
  startIcon: null,
  endIcon: null
};
function transformTheme$1(theme) {
  return {
    iconColor: theme.iconColor,
    primaryColor: theme.primaryColor,
    primaryColorRgb: new Color(theme.primaryColor).rgbArray().join(',')
  };
}
var SliderInput$1 = crown(themeCSS$1, withClassNames(styles$6, transformTheme$1))(SliderInput);
var _extends$4 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
var _createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }
  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();
function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _possibleConstructorReturn(self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return call && (typeof call === "object" || typeof call === "function") ? call : self;
}
function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }
  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
}
function throttleComponent(Component) {
  var ThrottledComponent = function (_React$Component) {
    _inherits(ThrottledComponent, _React$Component);
    function ThrottledComponent(props) {
      _classCallCheck(this, ThrottledComponent);
      var _this = _possibleConstructorReturn(this, (ThrottledComponent.__proto__ || Object.getPrototypeOf(ThrottledComponent)).call(this, props));
      _this.state = {
        value: props.value
      }; // eslint-disable-line react/prop-types
      _this.handleValueBlur = _this.handleValueBlur.bind(_this);
      _this.handleValueChange = _this.handleValueChange.bind(_this);
      return _this;
    }
    _createClass(ThrottledComponent, [{
      key: 'handleValueChange',
      value: function handleValueChange(value) {
        this.setState({
          value: value
        });
      }
    }, {
      key: 'handleValueBlur',
      value: function handleValueBlur(value) {
        if (!this.state.lastSentValue !== value) {
          this.props.onValueChange(value); // eslint-disable-line react/prop-types
          this.setState({
            lastSentValue: value
          });
        }
      }
    }, {
      key: 'render',
      value: function render() {
        return React.createElement(Component, _extends$4({}, this.props, {
          value: this.state.value,
          onValueBlur: this.handleValueBlur,
          onValueChange: this.handleValueChange
        }));
      }
    }]);
    return ThrottledComponent;
  }(React.Component);
  ThrottledComponent.propTypes = Object.assign({
    value: Component.propTypes.value
  },
  // `onValueBlur` omitted so that `throttledComponent` doesn't expect an `onBlur` event (only `Component` should need `onBlur` event)
  // `onValueChange` omitted so that there is no risk of this propType getting overridden by `Component`
  omit(Component.propTypes, 'onValueBlur', 'onValueChange'));
  ThrottledComponent.displayName = 'Throttled' + Component.displayName;
  return hoistNonReactStatic(ThrottledComponent, Component);
}
var styles$7 = {
  "bp-small": "(min-width: 500px)",
  "bp-medium": "(min-width: 768px)",
  "bp-large": "(min-width: 890px)",
  "wrapper": "SliderIntegerQuestion__wrapper___1iXXs",
  "sliderLabelWrapper": "SliderIntegerQuestion__sliderLabelWrapper___1Pppm",
  "sliderLabel": "SliderIntegerQuestion__sliderLabel___3GCfm",
  "slider": "SliderIntegerQuestion__slider___F8ZTK",
  "value": "SliderIntegerQuestion__value___4TwW0",
  "valueUnit": "SliderIntegerQuestion__valueUnit___3xzsl"
};

/*!********************************************************************************!*\
  !***  @energysavvy/customer-ui-components                                     ***!
  !***    ./src/questions/types/SliderIntegerQuestion/SliderIntegerQuestion.js  ***!
/*!********************************************************************************!*/

function SliderIntegerQuestion(_ref) {
  var labelId = _ref.labelId,
    question = _ref.question,
    onValueChange = _ref.onValueChange,
    onValueBlur = _ref.onValueBlur,
    className = _ref.className,
    helpTextId = _ref.helpTextId,
    value = _ref.value;
  var slug = question.slug,
    min = question.min,
    max = question.max,
    _question$step = question.step,
    step = _question$step === undefined ? 1 : _question$step,
    startIcon = question.start_icon,
    sliderColor = question.slider_color,
    sliderLabels = question.slider_labels,
    units = question.units;
  var labels = sliderLabels && sliderLabels.map(function (label) {
    return React.createElement('label', {
      key: label,
      htmlFor: slug + '-id',
      className: styles$7.sliderLabel
    }, label);
  });
  var theme = sliderColor ? {
    primaryColor: sliderColor,
    iconColor: sliderColor
  } : null;
  return React.createElement('div', {
    'aria-describedby': helpTextId,
    'aria-labelledby': labelId,
    className: classNames(styles$7.wrapper, className)
  }, labels ? React.createElement('span', {
    className: styles$7.sliderLabelWrapper
  }, labels) : null, React.createElement('span', {
    className: styles$7.value
  }, value, React.createElement('span', {
    className: styles$7.valueUnit
  }, units)), React.createElement('div', {
    className: styles$7.slider
  }, React.createElement(SliderInput$1, {
    name: slug,
    id: slug + '-id',
    min: min,
    max: max,
    startIcon: startIcon,
    theme: theme,
    step: step,
    value: value,
    valueLabel: value + ' ' + units,
    onValueBlur: onValueBlur,
    onValueChange: onValueChange
  })));
}
SliderIntegerQuestion.propTypes = {
  labelId: PropTypes.string.isRequired,
  question: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    question_text: PropTypes.string.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    slider_labels: PropTypes.arrayOf(PropTypes.string),
    start_icon: PropTypes.string,
    step: PropTypes.number,
    theme: PropTypes.string,
    slider_color: PropTypes.string,
    units: PropTypes.string
  }).isRequired,
  onValueChange: PropTypes.func.isRequired,
  onValueBlur: PropTypes.func.isRequired,
  className: PropTypes.string,
  helpTextId: PropTypes.string,
  value: function value(_ref2) {
    var question = _ref2.question,
      _value = _ref2.value;
    // eslint-disable-line consistent-return
    var isInStep = question.step ? (_value - question.min) % question.step === 0 : true;
    if (_value == null || _value > question.max || _value < question.min || !isInStep) {
      // eslint-disable-line eqeqeq, no-eq-null
      return new Error(_value + ' passed in as value, but not within min/max/step bounds.');
    }
  }
};
SliderIntegerQuestion.displayName = 'SliderIntegerQuestion';
var SliderIntegerQuestion$1 = throttleComponent(SliderIntegerQuestion);
var styles$8 = {
  "bp-medium": "(min-width: 768px)",
  "group": "SliderSelectQuestion__group___2nXkN",
  "label": "SliderSelectQuestion__label___3qSZF",
  "sliderLabel": "SliderSelectQuestion__sliderLabel___UjeVO",
  "slider": "SliderSelectQuestion__slider___1em1i"
};

/*!******************************************************************************!*\
  !***  @energysavvy/customer-ui-components                                   ***!
  !***    ./src/questions/types/SliderSelectQuestion/SliderSelectQuestion.js  ***!
/*!******************************************************************************!*/

function SliderSelectQuestion(_ref) {
  var labelId = _ref.labelId,
    question = _ref.question,
    onValueBlur = _ref.onValueBlur,
    onValueChange = _ref.onValueChange,
    className = _ref.className,
    helpTextId = _ref.helpTextId,
    value = _ref.value;
  function handleValueBlur(changedValue) {
    onValueBlur(question.choices[changedValue].slug);
  }
  function handleValueChange(changedValue) {
    onValueChange(question.choices[changedValue].slug);
  }
  var slug = question.slug,
    choices = question.choices,
    sliderLabelText = question.slider_label;
  var maxValue = choices.length - 1;
  var valueIndex = findIndex(choices, function (choice) {
    return choice.slug === value;
  });
  var valueLabel = choices[valueIndex].label;
  var sliderLabel = sliderLabelText ? React.createElement('p', {
    className: styles$8.sliderLabel
  }, sliderLabelText) : null; // eslint-disable-line camelcase

  return React.createElement('div', {
    'aria-describedby': helpTextId,
    'aria-labelledby': labelId,
    className: classNames(styles$8.group, className)
  }, sliderLabel, React.createElement('div', {
    className: styles$8.slider
  }, React.createElement(SliderInput$1, {
    name: slug,
    id: slug + '-id',
    min: 0,
    max: maxValue,
    value: valueIndex,
    valueLabel: valueLabel,
    onValueChange: handleValueChange,
    onValueBlur: handleValueBlur
  })), React.createElement('div', {
    className: styles$8.label
  }, valueLabel));
}
SliderSelectQuestion.propTypes = {
  labelId: PropTypes.string.isRequired,
  question: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    question_text: PropTypes.string.isRequired,
    choices: PropTypes.arrayOf(PropTypes.shape({
      slug: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })).isRequired,
    slider_label: PropTypes.string
  }).isRequired,
  onValueBlur: PropTypes.func.isRequired,
  onValueChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  helpTextId: PropTypes.string,
  value: function value(_ref2) {
    var question = _ref2.question,
      _value = _ref2.value;
    // eslint-disable-line consistent-return
    if (_value && findIndex(question.choices, function (choice) {
      return choice.slug === _value;
    }) === -1) {
      return new Error(_value + ' passed in as value, but not present in question.choices.');
    }
  }
};
SliderSelectQuestion.displayName = 'SliderSelectQuestion';
var SliderSelectQuestion$1 = throttleComponent(SliderSelectQuestion);
var _extends$5 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
function _toConsumableArray(arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) {
      arr2[i] = arr[i];
    }
    return arr2;
  } else {
    return Array.from(arr);
  }
}
function MultiSelectQuestion(props) {
  var question = props.question,
    value = props.value,
    onValueChange = props.onValueChange; // eslint-disable-line react/prop-types

  var choices = question.choices,
    noneChoiceLabel = question.noneChoiceLabel;
  var modifiedQuestion = noneChoiceLabel ? Object.assign({}, question, {
    choices: [].concat(_toConsumableArray(choices), [{
      slug: '__none__',
      label: noneChoiceLabel
    }])
  }) : question;
  return React.createElement(BaseSelectQuestion, _extends$5({
    handleChange: function handleChange(event) {
      // Sets answer to an empty array if none apply
      if (/__none__/.test(event.target.value)) {
        onValueChange([]);
        return;
      }
      var selectedTiles = xor(value, [event.target.value]);
      // Clears out the answer if all choices have been unselected
      var newAnswer = selectedTiles.length === 0 ? null : selectedTiles;
      onValueChange(newAnswer);
    },
    choiceType: 'checkbox',
    isSelected: function isSelected(choiceSlug) {
      if (!Array.isArray(value)) {
        return false;
      }
      return /__none__/.test(choiceSlug) ? value.length === 0 : value.indexOf(choiceSlug) !== -1;
    }
  }, props, {
    question: modifiedQuestion
  }));
}
MultiSelectQuestion.propTypes = {
  labelId: BaseSelectQuestion.propTypes.labelId,
  question: BaseSelectQuestion.propTypes.question,
  className: BaseSelectQuestion.propTypes.className,
  helpTextId: BaseSelectQuestion.propTypes.helpTextId,
  onValueChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  noneChoiceLabel: PropTypes.string
};
var styles$9 = {
  "bp-medium": "(min-width: 768px)",
  "wrapper": "TextQuestion__wrapper___2WQLv"
};

/*!**************************************************************!*\
  !***  @energysavvy/customer-ui-components                   ***!
  !***    ./src/questions/types/TextQuestion/TextQuestion.js  ***!
/*!**************************************************************!*/

function TextQuestion(_ref) {
  var labelId = _ref.labelId,
    slug = _ref.question.slug,
    onValueChange = _ref.onValueChange,
    className = _ref.className,
    value = _ref.value,
    helpTextId = _ref.helpTextId;
  function handleChange(event) {
    if (event.target.value === '') {
      onValueChange(null);
    } else {
      onValueChange(event.target.value);
    }
  }
  return React.createElement('div', {
    'aria-describedby': helpTextId,
    'aria-labelledby': labelId,
    className: classNames(styles$9.wrapper, className)
  }, React.createElement(Input, {
    type: 'text',
    slug: slug,
    onChange: handleChange,
    value: value
  }));
}
TextQuestion.propTypes = {
  labelId: PropTypes.string.isRequired,
  question: PropTypes.shape({
    slug: PropTypes.string.isRequired
  }).isRequired,
  onValueChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  value: PropTypes.string,
  helpTextId: PropTypes.string
};
TextQuestion.defaultProps = {
  value: ''
};

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/questions/types/index.js     ***!
/*!*********************************************!*/

var styles$a = {
  "bp-medium": "(min-width: 768px)",
  "mantis-green": "#6fae6c",
  "light-blue": "#BDD7F1",
  "base": "StatusMessage__base___3XU3l",
  "warning": "StatusMessage__warning___1DQP9 StatusMessage__base___3XU3l",
  "done": "StatusMessage__done___Z1Aun StatusMessage__base___3XU3l"
};

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/questions/StatusMessage.js   ***!
/*!*********************************************!*/

function StatusMessage(_ref) {
  var className = _ref.className,
    status = _ref.status,
    statusMessage = _ref.statusMessage;
  return React.createElement('span', {
    className: classNames(styles$a[status], className)
  }, statusMessage);
}
StatusMessage.propTypes = {
  className: PropTypes.string,
  status: PropTypes.oneOf(['normal', 'warning', 'done']),
  statusMessage: PropTypes.string
};
var styles$b = {
  "bp-medium": "(min-width: 768px)",
  "wrapper": "StatusBar__wrapper___3lV2W"
};

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/questions/StatusBar.js       ***!
/*!*********************************************!*/

var BUTTON_THEME = {
  primaryColor: '#10A021',
  // `pigment-green`
  primaryColorDark: '#0D861C',
  textOnPrimary: '#FFFFFF'
};
function StatusBar(_ref) {
  var status = _ref.status,
    statusMessage = _ref.statusMessage,
    buttonConfig = _ref.buttonConfig,
    className = _ref.className;
  var actionButtonElement = status === 'done' && buttonConfig ? React.createElement(Button, {
    text: buttonConfig.text,
    onClick: buttonConfig.action,
    padding: Button.PADDING.COMPACT,
    theme: BUTTON_THEME
  }) : null;
  return React.createElement('span', {
    className: classNames(styles$b.wrapper, className)
  }, React.createElement(StatusMessage, {
    statusMessage: statusMessage,
    status: status
  }), actionButtonElement);
}
StatusBar.propTypes = {
  status: PropTypes.oneOf(['normal', 'warning', 'done']).isRequired,
  statusMessage: PropTypes.string.isRequired,
  buttonConfig: PropTypes.shape({
    action: PropTypes.func.isRequired,
    text: PropTypes.string.isRequired
  }),
  className: PropTypes.string
};
var styles$c = {
  "darker-gray": "#666",
  "light-gray": "#e3e3e3",
  "description": "QuestionDescription__description___1Jy5K"
};

/*!**************************************************!*\
  !***  @energysavvy/customer-ui-components       ***!
  !***    ./src/questions/QuestionDescription.js  ***!
/*!**************************************************!*/

function QuestionDescription(_ref) {
  var description = _ref.description,
    layoutClassName = _ref.layoutClassName;
  var markdown = remark().use(remarkReact).processSync(description).contents;
  return React.createElement('div', {
    className: classNames(styles$c.description, layoutClassName)
  }, React.cloneElement(markdown));
}
QuestionDescription.displayName = 'QuestionDescription';
QuestionDescription.propTypes = {
  description: PropTypes.string,
  layoutClassName: PropTypes.string
};
var styles$d = {
  "bp-medium": "(min-width: 768px)",
  "heading": "QuestionHeading__heading___21_2K"
};

/*!***************************************************!*\
  !***  @energysavvy/customer-ui-components        ***!
  !***    ./src/questions/QuestionHeading.css.raw  ***!
/*!***************************************************!*/

var themeCSS$2 = ".styleSelector {\n    color: questionHeadingColor;\n    /* Coming directly from theming */\n    /* stylelint-disable font-family-name-quotes */\n    font-family: questionHeadingFontFamily;\n    /* stylelint-enable font-family-name-quotes */\n}\n";

/*!**********************************************!*\
  !***  @energysavvy/customer-ui-components   ***!
  !***    ./src/questions/QuestionHeading.js  ***!
/*!**********************************************!*/

function QuestionHeading(_ref) {
  var crownClassName = _ref.crownClassName,
    layoutClassName = _ref.layoutClassName,
    headingId = _ref.headingId,
    heading = _ref.heading;
  return React.createElement('h3', {
    className: classNames(styles$d.heading, crownClassName, layoutClassName),
    id: headingId,
    tabIndex: '0'
  }, heading);
}
QuestionHeading.propTypes = {
  crownClassName: PropTypes.string,
  layoutClassName: PropTypes.string,
  headingId: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired
};
QuestionHeading.displayName = 'QuestionHeading';
var QuestionHeading$1 = crown(themeCSS$2)(QuestionHeading);
var styles$e = {
  "bp-medium": "(min-width: 768px)",
  "faint-green": "#FBFCF9",
  "light-gray": "#e3e3e3",
  "base": "ActiveQuestions__base___2ISxG",
  "normal": "ActiveQuestions__normal___3SFKA ActiveQuestions__base___2ISxG",
  "warning": "ActiveQuestions__warning___1sa-A ActiveQuestions__base___2ISxG",
  "done": "ActiveQuestions__done___2acYg ActiveQuestions__base___2ISxG",
  "questions": "ActiveQuestions__questions___2tRbR",
  "question": "ActiveQuestions__question___Xpom5"
};
var layout = {
  "bp-medium": "(min-width: 768px)",
  "icon-width": "50px",
  "wrapper": "ActiveQuestions-layout__wrapper___2wJq6",
  "title": "ActiveQuestions-layout__title___1jInI",
  "description": "ActiveQuestions-layout__description___349GN",
  "helpText": "ActiveQuestions-layout__helpText___2fQ76",
  "questions": "ActiveQuestions-layout__questions___1su2b",
  "status": "ActiveQuestions-layout__status___Ci1UU"
};

/*!**********************************************!*\
  !***  @energysavvy/customer-ui-components   ***!
  !***    ./src/questions/ActiveQuestions.js  ***!
/*!**********************************************!*/

function getComponentType(type) {
  switch (type) {
    case 'select':
    case 'SelectQuestion':
      return SelectQuestion;
    case 'select_slider':
    case 'SliderSelectQuestion':
      return SliderSelectQuestion$1;
    case 'integer_slider':
    case 'SliderIntegerQuestion':
      return SliderIntegerQuestion$1;
    case 'multiselect':
    case 'MultiSelectQuestion':
      return MultiSelectQuestion;
    case 'text':
    case 'TextQuestion':
      return TextQuestion;
    default:
      return SelectQuestion;
  }
}
function ActiveQuestions(_ref) {
  var questions = _ref.questions,
    updateAnswerState = _ref.updateAnswerState,
    answers = _ref.answers,
    buttonConfig = _ref.buttonConfig,
    helpTextPrefix = _ref.helpTextPrefix,
    status = _ref.status,
    statusMessage = _ref.statusMessage;
  var _questions$ = questions[0],
    firstQuestionSlug = _questions$.slug,
    firstQuestionDescription = _questions$.description,
    firstQuestionHelpText = _questions$.help_text,
    firstQuestionText = _questions$.question_text;
  var headingId = firstQuestionSlug + '-heading';
  var helpTextId = firstQuestionHelpText ? firstQuestionSlug + '-help-text' : null;
  var helpTextElement = helpTextId ? React.createElement(HelpText, {
    className: layout.helpText,
    id: helpTextId,
    prefix: helpTextPrefix,
    text: firstQuestionHelpText
  }) : null;
  var statusBarElement = status !== 'normal' && statusMessage ? React.createElement(StatusBar, {
    className: layout.status,
    buttonConfig: buttonConfig,
    status: status,
    statusMessage: statusMessage
  }) : null;
  var questionComponents = questions.map(function (question) {
    function onValueChange(value) {
      updateAnswerState(question.slug, value);
    }
    var slug = question.slug;
    var QuestionComponent = getComponentType(question.component);
    return React.createElement(QuestionComponent, {
      key: slug,
      className: styles$e.question,
      labelId: headingId,
      helpTextId: helpTextId,
      question: question,
      onValueChange: onValueChange,
      value: answers[slug]
    });
  });
  return React.createElement('div', {
    className: classNames(styles$e[status], layout.wrapper)
  }, React.createElement(QuestionHeading$1, {
    layoutClassName: layout.title,
    headingId: headingId,
    heading: firstQuestionText
  }), firstQuestionDescription && React.createElement(QuestionDescription, {
    description: firstQuestionDescription,
    layoutClassName: layout.description
  }), statusBarElement, helpTextElement, React.createElement('div', {
    className: classNames(styles$e.questions, layout.questions)
  }, questionComponents));
}
ActiveQuestions.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.shape({
    question_text: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    help_text: PropTypes.string,
    description: PropTypes.string
  })).isRequired,
  updateAnswerState: PropTypes.func.isRequired,
  answers: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  buttonConfig: PropTypes.shape({
    action: PropTypes.func.isRequired,
    text: PropTypes.string.isRequired
  }),
  helpTextPrefix: PropTypes.string,
  status: PropTypes.oneOf(['normal', 'warning', 'done']),
  statusMessage: PropTypes.string
};
ActiveQuestions.defaultProps = {
  answers: {},
  status: 'normal'
};
var styles$f = {
  "bp-medium": "(min-width: 768px)",
  "mint-green": "#f5f8ef",
  "mid-gray": "#ddd",
  "darker-gray": "#666",
  "lighter-gray": "#eee",
  "dark-gray": "#888",
  "changeButton": "SelectQuestionSummary__changeButton___3Lvuo",
  "icon": "SelectQuestionSummary__icon___d0ci9",
  "change": "SelectQuestionSummary__change___19Yxp",
  "contentWrapper": "SelectQuestionSummary__contentWrapper___3Snbp"
};

/*!****************************************************!*\
  !***  @energysavvy/customer-ui-components         ***!
  !***    ./src/questions/SelectQuestionSummary.js  ***!
/*!****************************************************!*/

function SelectQuestionSummary(_ref) {
  var question = _ref.question,
    changeSelectedAnswerText = _ref.changeSelectedAnswerText,
    selectedChoice = _ref.selectedChoice,
    unselectChoice = _ref.unselectChoice,
    className = _ref.className;
  var _handleClick = function _handleClick() {
    unselectChoice(question.slug, null);
  };
  var summaryDisplay = selectedChoice.icon ? React.createElement('img', {
    src: selectedChoice.icon,
    className: styles$f.icon,
    alt: ''
  }) : selectedChoice.label;
  return React.createElement('button', {
    className: classNames(styles$f.changeButton, className),
    'aria-label': changeSelectedAnswerText + ' ' + question.label,
    onClick: _handleClick
  }, React.createElement('span', {
    className: styles$f.contentWrapper
  }, summaryDisplay, React.createElement('span', {
    className: styles$f.change
  }, changeSelectedAnswerText)));
}
SelectQuestionSummary.propTypes = {
  question: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  }).isRequired,
  changeSelectedAnswerText: PropTypes.string.isRequired,
  selectedChoice: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.string
  }).isRequired,
  unselectChoice: PropTypes.func.isRequired,
  className: PropTypes.string
};
SelectQuestionSummary.defaultProps = {
  changeSelectedAnswerText: 'Change'
};
var styles$g = {
  "mantis-green": "#6fae6c",
  "glaucous-blue": "#7E98B0",
  "size-pixels": "50px",
  "base": "StatusIcon__base___1cScy",
  "material-icons": "StatusIcon__material-icons____bkzI",
  "icon": "StatusIcon__icon___2HDAU StatusIcon__material-icons____bkzI",
  "warning": "StatusIcon__warning___5dtiY StatusIcon__base___1cScy",
  "done": "StatusIcon__done___2SBh9 StatusIcon__base___1cScy"
};

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/questions/StatusIcon.js      ***!
/*!*********************************************!*/

// Duplication used to avoid problems with escaping ampersand
var CHECK = React.createElement('i', {
  className: styles$g.icon
}, "\uE5CA");
var PRIORITY_HIGH = React.createElement('i', {
  className: styles$g.icon
}, "\uE645");
function StatusIcon(_ref) {
  var status = _ref.status,
    className = _ref.className;
  return React.createElement('div', {
    className: classNames(styles$g[status], className)
  }, status === 'done' ? CHECK : PRIORITY_HIGH);
}
StatusIcon.propTypes = {
  status: PropTypes.oneOf(['warning', 'done']).isRequired,
  className: PropTypes.string
};
var styles$h = {
  "medium-pixels": "768px",
  "bp-medium": "(min-width: 768px)",
  "light-gray": "#e3e3e3",
  "faint-green": "#FBFCF9",
  "base": "QuestionTray__base___3xCQf _boxsizing__borderBox___2GxsM",
  "normal": "QuestionTray__normal___3uY7g QuestionTray__base___3xCQf _boxsizing__borderBox___2GxsM",
  "warning": "QuestionTray__warning___gk4pA QuestionTray__base___3xCQf _boxsizing__borderBox___2GxsM",
  "done": "QuestionTray__done___pJUlS QuestionTray__base___3xCQf _boxsizing__borderBox___2GxsM",
  "summaries": "QuestionTray__summaries___252OQ"
};
var layout$1 = {
  "summary": "QuestionTray-layout__summary___2h7QN",
  "statusIcon": "QuestionTray-layout__statusIcon___18Y17"
};

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/questions/QuestionTray.js    ***!
/*!*********************************************!*/

function getLastWithoutDisplayPreviousIndex(questions) {
  for (var i = questions.length - 1; i >= 0; --i) {
    if (!questions[i].display_previous) {
      return i;
    }
  }
  return 0;
}
function getActiveQuestions(questions, answers) {
  if (questions.length === 1) {
    return {
      firstDisplayedIndex: 0,
      firstNotDisplayedIndex: 1
    };
  }
  var firstUnansweredIndex = questions.findIndex(function (question) {
    return !answers[question.slug];
  });
  var firstDisplayedIndex = firstUnansweredIndex === -1 ? getLastWithoutDisplayPreviousIndex(questions) : firstUnansweredIndex;
  var firstWithoutDisplayPreviousIndex = questions.findIndex(function (question, index) {
    if (index <= firstDisplayedIndex) {
      return false;
    }
    return !question.display_previous;
  });
  var firstNotDisplayedIndex = firstWithoutDisplayPreviousIndex === -1 ? questions.length : firstWithoutDisplayPreviousIndex;
  return {
    firstDisplayedIndex: firstDisplayedIndex,
    firstNotDisplayedIndex: firstNotDisplayedIndex
  };
}
function QuestionTray(_ref) {
  var answers = _ref.answers,
    questions = _ref.questions,
    updateAnswerState = _ref.updateAnswerState,
    buttonConfig = _ref.buttonConfig,
    helpTextPrefix = _ref.helpTextPrefix,
    changeSelectedAnswerText = _ref.changeSelectedAnswerText,
    status = _ref.status,
    statusMessage = _ref.statusMessage;

  // Split the last questions from the list
  var _getActiveQuestions = getActiveQuestions(questions, answers),
    firstDisplayedIndex = _getActiveQuestions.firstDisplayedIndex,
    firstNotDisplayedIndex = _getActiveQuestions.firstNotDisplayedIndex;

  // Only SelectQuestions are allowed to be summarized

  var summarizedQuestions = questions.slice(0, firstDisplayedIndex);
  var activeQuestions = questions.slice(firstDisplayedIndex, firstNotDisplayedIndex);

  // Display all but the last questions in compact form
  var selectedQuestionSummaries = summarizedQuestions.map(function (question) {
    var selectedChoice = question.choices.find(function (choice) {
      return choice.slug === answers[question.slug];
    });
    return React.createElement(SelectQuestionSummary, {
      className: layout$1.summary,
      key: question.slug,
      question: question,
      changeSelectedAnswerText: changeSelectedAnswerText,
      selectedChoice: selectedChoice,
      unselectChoice: updateAnswerState
    });
  });
  var statusIconElement = status !== 'normal' ? React.createElement(StatusIcon, {
    status: status,
    className: layout$1.statusIcon
  }) : null;
  return React.createElement('div', {
    className: styles$h[status]
  }, React.createElement('div', {
    'aria-live': 'assertive',
    className: styles$h.summaries
  }, selectedQuestionSummaries), React.createElement(ActiveQuestions, {
    questions: activeQuestions,
    answers: answers,
    buttonConfig: buttonConfig,
    helpTextPrefix: helpTextPrefix,
    status: status,
    statusMessage: statusMessage,
    updateAnswerState: updateAnswerState
  }), statusIconElement);
}
QuestionTray.propTypes = {
  answers: PropTypes.object.isRequired,
  // eslint-disable-line react/forbid-prop-types
  questions: ActiveQuestions.propTypes.questions,
  updateAnswerState: PropTypes.func.isRequired,
  buttonConfig: PropTypes.shape({
    action: PropTypes.func.isRequired,
    text: PropTypes.string.isRequired
  }),
  helpTextPrefix: PropTypes.string,
  changeSelectedAnswerText: PropTypes.string,
  status: PropTypes.oneOf(['normal', 'warning', 'done']),
  statusMessage: PropTypes.string
};
QuestionTray.defaultProps = {
  status: 'normal'
};
var styles$i = {
  "bp-medium": "(min-width: 768px)",
  "wrapper": "SectionProgressIndicator__wrapper___2gH1d _boxsizing__borderBox___2GxsM",
  "sections": "SectionProgressIndicator__sections___fyk_J"
};
var styles$j = {
  "bp-small": "(min-width: 500px)",
  "text-quiet": "#6f8590",
  "chevron": "Section__chevron___38EJ1 chevron__chevron___1q-tJ",
  "common": "Section__common___39H_i",
  "inActive": "Section__inActive___1s6Qw Section__common___39H_i",
  "active": "Section__active___3dGxL Section__common___39H_i",
  "name": "Section__name___2T4DY",
  "index": "Section__index___3Wsqy",
  "complete": "Section__complete___1PPoi"
};
var visuallyhiddenStyles = {
  "visuallyhidden": "_visuallyhidden__visuallyhidden___GfPHq",
  "focusable": "_visuallyhidden__focusable___3RYRJ"
};

/*!**********************************************************!*\
  !***  @energysavvy/customer-ui-components               ***!
  !***    ./src/SectionProgressIndicator/Section.css.raw  ***!
/*!**********************************************************!*/

var themeCSS$3 = ".styleSelector {\n    color: primaryColor;\n}\n";
function _defineProperty$1(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function Section(_ref) {
  var _classNames;
  var index = _ref.index,
    isCurrentSection = _ref.isCurrentSection,
    isComplete = _ref.isComplete,
    name = _ref.name,
    crownClassName = _ref.crownClassName;
  var wrapperClassName = classNames((_classNames = {}, _defineProperty$1(_classNames, crownClassName, isCurrentSection), _defineProperty$1(_classNames, styles$j.active, isCurrentSection), _defineProperty$1(_classNames, styles$j.complete, isComplete), _defineProperty$1(_classNames, styles$j.inActive, !isCurrentSection), _classNames));
  var status = isCurrentSection ? React.createElement('span', {
    className: visuallyhiddenStyles.visuallyhidden
  }, '(current)') : null;
  return React.createElement('li', {
    className: wrapperClassName
  }, React.createElement('span', {
    className: styles$j.index
  }, index + 1), React.createElement('span', {
    className: styles$j.name
  }, name), status, React.createElement('span', {
    className: styles$j.chevron
  }));
}
Section.displayName = 'Section';
Section.propTypes = {
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  crownClassName: PropTypes.string,
  isCurrentSection: PropTypes.bool,
  isComplete: PropTypes.bool
};
var Section$1 = crown(themeCSS$3)(Section);

/*!**********************************************************************!*\
  !***  @energysavvy/customer-ui-components                           ***!
  !***    ./src/SectionProgressIndicator/SectionProgressIndicator.js  ***!
/*!**********************************************************************!*/

function SectionProgressIndicator(_ref) {
  var sections = _ref.sections,
    completedSections = _ref.completedSections,
    currentSection = _ref.currentSection,
    theme = _ref.theme;
  var sectionComponents = sections.map(function (section, index) {
    return React.createElement(Section$1, {
      key: section,
      name: section,
      index: index,
      isCurrentSection: section === currentSection,
      isComplete: completedSections.indexOf(section) !== -1,
      theme: theme
    });
  });
  return React.createElement('div', {
    className: styles$i.wrapper
  }, React.createElement('ol', {
    className: styles$i.sections
  }, sectionComponents));
}
SectionProgressIndicator.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.string).isRequired,
  completedSections: function completedSections(_ref2) {
    var sections = _ref2.sections,
      _completedSections = _ref2.completedSections;
    // eslint-disable-line consistent-return
    if (!Array.isArray(_completedSections)) {
      return new Error('completedSections must be an Array! Receieved ' + _completedSections);
    }
    var unavailableSection = _completedSections.find(function (section) {
      return sections.indexOf(section) === -1;
    });
    if (unavailableSection) {
      return new Error(unavailableSection + ' passed in as completed, but not present in sections');
    }
  },
  currentSection: function currentSection(_ref3) {
    var sections = _ref3.sections,
      _currentSection = _ref3.currentSection;
    // eslint-disable-line consistent-return
    if (_currentSection && sections.indexOf(_currentSection) === -1) {
      return new Error(_currentSection + ' passed in as current, but not present in sections');
    }
  },
  theme: Section$1.propTypes.theme
};
SectionProgressIndicator.defaultProps = {
  completedSections: []
};
var styles$k = {
  "wrapper": "AppContainer__wrapper___kCA2y"
};

/*!***************************************************!*\
  !***  @energysavvy/customer-ui-components        ***!
  !***    ./src/AppContainer/AppContainer.css.raw  ***!
/*!***************************************************!*/

var themeCSS$4 = "#energysavvyAppContainer .wrapper a {\n    color: linkColor;\n    text-decoration: linkTextDecoration;\n}\n";
var _extends$6 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
function AppContainer(props) {
  var className = classNames(props.crownClassName, styles$k.wrapper);
  return React.createElement(ThemeProvider, {
    theme: props.theme,
    transform: withPrefix('#energysavvyAppContainer.energysavvyAppContainer', {
      exclude: /#energysavvyAppContainer/
    })
  }, React.createElement('div', _extends$6({}, omit(props, 'children', 'crownClassName', 'theme'), {
    className: className
  }), props.children));
}
AppContainer.propTypes = {
  children: PropTypes.node.isRequired,
  crownClassName: PropTypes.string,
  theme: PropTypes.shape({
    linkColor: PropTypes.string.isRequired,
    linkTextDecoration: PropTypes.string.isRequired
  })
};
AppContainer.displayName = 'AppContainer';
function transformTheme$2(theme) {
  return {
    linkColor: theme.linkColor,
    linkTextDecoration: theme.linkTextDecoration
  };
}
var AppContainer$1 = crown(themeCSS$4, withClassNames(styles$k, transformTheme$2))(AppContainer);
var styles$l = {
  "appear": "index__appear___1TIB_",
  "appearActive": "index__appearActive___2pAru"
};
var _extends$7 = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
function _objectWithoutProperties(obj, keys) {
  var target = {};
  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }
  return target;
}
function FadeInOnMount(_ref) {
  var children = _ref.children,
    component = _ref.component,
    props = _objectWithoutProperties(_ref, ['children', 'component']);
  return React.createElement(CSSTransitionGroup, _extends$7({
    component: component,
    transitionName: {
      appear: styles$l.appear,
      appearActive: styles$l.appearActive
    },
    transitionAppear: true,
    transitionAppearTimeout: 300,
    transitionEnter: false,
    transitionLeave: false
  }, props), children);
}
FadeInOnMount.propTypes = {
  children: PropTypes.node.isRequired,
  component: PropTypes.node
};
FadeInOnMount.defaultProps = {
  component: 'div'
};
FadeInOnMount.displayName = 'FadeInOnMount';

/*!*********************************************!*\
  !***  @energysavvy/customer-ui-components  ***!
  !***    ./src/index.js                     ***!
/*!*********************************************!*/

export { Alert, AnchorButton, Button, Input, LoadingSpinner, QuestionTray, SelectQuestion, SliderSelectQuestion$1 as SliderSelectQuestion, MultiSelectQuestion, TextQuestion, SelectQuestionSummary, SliderInput$1 as SliderInput, SectionProgressIndicator, AppContainer$1 as AppContainer, FadeInOnMount };